import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AuthChangeEvent } from "@supabase/supabase-js";
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSupabaseClient } from "@/services/supabase";

const MIN_PASSWORD_LENGTH = 8;

function isValidPassword(pw: string): string | null {
  if (!pw) return "Password is required.";
  if (pw.length < MIN_PASSWORD_LENGTH)
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`;
  if (!/[A-Za-z]/.test(pw))
    return "Password must contain at least one letter.";
  if (!/[0-9]/.test(pw))
    return "Password must contain at least one number.";
  return null;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Detect a genuine Supabase password-recovery flow.
  //
  // Register the listener before checking the current session so the page can
  // receive PASSWORD_RECOVERY while Supabase processes the email-link token.
  // Ordinary authenticated sessions are denied after a short grace period.
  // Once recovery is detected, the denial timer is cancelled and must never
  // revoke the valid recovery state.
  useEffect(() => {
    let cancelled = false;
    let recoveryDetected = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const supabase = getSupabaseClient();

    const authorizeRecovery = () => {
      recoveryDetected = true;
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
      if (!cancelled) {
        setHasRecoverySession(true);
        setLoading(false);
      }
    };

    // Subscribe first so a fast URL-token exchange cannot race ahead of this
    // page and leave a valid recovery session looking expired.
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent) => {
        if (cancelled) return;
        if (event === "PASSWORD_RECOVERY") {
          authorizeRecovery();
        }
      }
    );

    async function checkInitialSession() {
      try {
        // This lets Supabase complete any in-flight URL session detection.
        // A session alone is not enough to authorize password recovery; only
        // PASSWORD_RECOVERY enables the form.
        await supabase.auth.getSession();
      } catch {
        if (!cancelled && !recoveryDetected) {
          setHasRecoverySession(false);
          setLoading(false);
        }
      }
    }

    checkInitialSession();

    timeout = setTimeout(() => {
      if (!cancelled && !recoveryDetected) {
        setHasRecoverySession(false);
        setLoading(false);
      }
    }, 3000);

    // Protect against an implementation that emits PASSWORD_RECOVERY
    // synchronously while the listener is being registered.
    if (recoveryDetected && timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }

    return () => {
      cancelled = true;
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      subscription.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLocalError(null);

    const pwError = isValidPassword(newPassword);
    if (pwError) {
      setLocalError(pwError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      // updateUser() in the context of a recovery session updates the
      // authenticated user's password. Supabase enforces that the caller
      // must have a valid recovery session/token.
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setLocalError(error.message || "Password update failed. Please try again.");
        return;
      }
      // End the recovery session by signing out, so the old password
      // can no longer be used via this session.
      await supabase.auth.signOut();
      setDone(true);
    } catch {
      setLocalError("Password update failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background" data-testid="reset-password-page">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verifying recovery link...</p>
        </div>
      </main>
    );
  }

  if (!hasRecoverySession) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <Card className="border-border/80 shadow-soft" data-testid="reset-password-invalid-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Invalid or expired recovery link</CardTitle>
              <CardDescription>
                This password recovery link is invalid, has expired, or has already been used.
                Please request a new recovery link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" variant="outline" onClick={() => navigate("/forgot-password")} data-testid="reset-password-request-new">
                <ArrowLeft className="mr-2 size-4" />
                Request a new recovery link
              </Button>
              <div className="mt-3 text-center">
                <Button variant="ghost" className="text-sm" onClick={() => navigate("/login")} data-testid="reset-password-back-to-login-invalid">
                  Back to login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  if (done) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <Card className="border-border/80 shadow-soft" data-testid="reset-password-success-card">
            <CardHeader className="pb-4">
              <div className="mb-2 flex justify-center">
                <CheckCircle2 className="size-12 text-primary" data-testid="reset-password-success-icon" />
              </div>
              <CardTitle className="text-center text-lg">Password updated</CardTitle>
              <CardDescription className="text-center">
                Your password has been changed successfully. Please sign in with your new password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate("/login")} data-testid="reset-password-back-to-login">
                <ArrowLeft className="mr-2 size-4" />
                Back to login
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <p className="text-sm font-semibold text-primary" data-testid="reset-password-eyebrow">ACZON Farms</p>
          <h2 className="mt-1 text-3xl font-semibold tracking-tight" data-testid="reset-password-title">Set a new password</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground" data-testid="reset-password-description">
            Enter your new password below. Your old password will no longer work after this change.
          </p>
        </div>

        <Card className="border-border/80 shadow-soft" data-testid="reset-password-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Reset password</CardTitle>
            <CardDescription>Choose a strong password for your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                    className="pr-11"
                    data-testid="reset-password-new-input"
                  />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setShowPassword((v) => !v)} className="absolute right-1 top-1 size-8" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="reset-password-visibility-button">
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                  className="pr-11"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <p className="text-xs leading-5 text-muted-foreground" data-testid="reset-password-hint">
                Password must be at least {MIN_PASSWORD_LENGTH} characters and include a letter and a number.
              </p>
              {localError && (
                <p className="text-sm font-medium text-destructive" data-testid="reset-password-error-message" role="alert">
                  {localError}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting || !newPassword || !confirmPassword} data-testid="reset-password-submit-button">
                {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <LockKeyhole className="mr-2 size-4" />}
                {submitting ? "Updating..." : "Update password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
